import cn from 'classnames';
import React, { FC, ReactNode, memo, useCallback } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select as SelectMUI,
  SelectProps,
  Typography,
  useTheme,
} from '@mui/material';

import { Size } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type Map<T extends string | number> = {
  [key in T]: string;
};

export type OptionType = {
  name: string;
  value: string | number;
  subLabel?: string;
  icon?: string;
  startIcon?: string;
};

type Props<T extends string | number> = SelectProps & {
  value: T | T[];
  options: OptionType[];
  className?: string;
  placeholder?: string;
  classNameWrap?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  mapName?: Map<T>;
  isDark?: boolean;
  title?: string;
  classNameItemText?: string;
};

const Select: FC<Props<string | number>> = ({
  className = '',
  placeholder = '',
  classNameWrap = '',
  options,
  disabled,
  errorMessage = null,
  value,
  mapName = undefined,
  multiple,
  isDark,
  title,
  classNameItemText,
  ...props
}) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const renderSingleSelectValue = useCallback(
    (selectedEl: any) => {
      const selectedItem = options.find((el) => el.value === selectedEl);
      return selectedItem ? (
        <span className={classes.selectedMenuItem}>
          {selectedItem?.icon && (
            <span className={classes.menuIcon}>
              <img src={selectedItem.icon} alt="" />
            </span>
          )}
          {selectedItem?.startIcon && (
            <span className={classes.menuIcon}>
              <img src={selectedItem.startIcon} alt="" />
            </span>
          )}
          <Typography variant="subtitle1">{selectedItem.name}</Typography>
        </span>
      ) : (
        placeholder
      );
    },
    [options, placeholder],
  );

  const renderMultiSelectValue = useCallback(
    (items: any): string | ReactNode => {
      if (items.length === 0 && placeholder) {
        return placeholder;
      }

      const node: ReactNode = items.map(
        (val: string | number, index: number) => {
          const selectedItem = options.find((el) => el.value === val);
          const isLastItem = index === items.length - 1;

          return (
            <span key={`${val}_${index.toString()}`}>
              {mapName?.[val] ?? selectedItem?.name ?? val}
              {!isLastItem && ', '}
            </span>
          );
        },
      );

      return node;
    },
    [mapName, options, placeholder],
  );

  return (
    <div>
      {title && (
        <Typography
          variant="caption1"
          className={classes.selectTitle}
          color={(color) => color.secondary.s3}
        >
          {title}
        </Typography>
      )}
      <FormControl
        className={cn(
          classes.box,
          { [classes.disabledBox]: disabled },
          { [classes.errorBox]: errorMessage },
          { [classes.darkBox]: isDark },
          classNameWrap,
        )}
      >
        <SelectMUI
          value={value}
          className={cn(
            classes.select,
            { [classes.darkSelect]: isDark },
            className,
          )}
          disabled={disabled}
          IconComponent={ExpandMoreIcon}
          size={Size.Small}
          multiple={multiple}
          renderValue={
            multiple ? renderMultiSelectValue : renderSingleSelectValue
          }
          displayEmpty
          {...props}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 250,
              },
              className: classes.dropDown,
            },
          }}
        >
          {options.map(
            (
              { name, value: valueOption, icon, startIcon, subLabel },
              index,
            ) => (
              <MenuItem
                key={`${valueOption}_${index.toString()}`}
                value={valueOption}
                className={classes.menuItem}
                disabled={valueOption === value}
                sx={{
                  '&.Mui-selected': {
                    color: theme.primary.p2,
                    background: theme.secondary.s4,
                    '&:hover': {
                      background: theme.secondary.s4,
                    },
                  },
                  '&:hover': {
                    background: theme.secondary.s4,
                    color: theme.primary.p2,
                  },
                }}
              >
                {multiple && (
                  <div className={classes.checkbox}>
                    <Checkbox
                      checked={
                        Array.isArray(value) && value.indexOf(valueOption) > -1
                      }
                      color="primary"
                    />
                  </div>
                )}
                <span className={classes.item}>
                  {startIcon && (
                    <span className={classes.iconMenu}>
                      <img src={startIcon} alt="" />
                    </span>
                  )}
                  <ListItemText
                    primary={mapName?.[valueOption] ?? name}
                    className={classNameItemText}
                  />
                  {subLabel && (
                    <Typography
                      variant="caption1"
                      className={classes.subLabel}
                      color={(color) => color.secondary.s3}
                    >
                      {subLabel}
                    </Typography>
                  )}
                  {icon && (
                    <span className={classes.iconMenu}>
                      <img src={icon} alt="" />
                    </span>
                  )}
                </span>
              </MenuItem>
            ),
          )}
        </SelectMUI>
      </FormControl>
    </div>
  );
};

export default memo(Select);
